import React, { ReactElement } from 'react'
import { Flex, Text, get } from 'theme-ui'
import Svg from 'components/UI/SVG'
import HomeIcon from 'images/home-icon.svg'
import BreadcrumbArrow from 'images/breadcrumb-arrow.svg'
import InternalLink from 'components/UI/InternalLink'
import ExternalLink from 'components/UI/ExternalLink'

export interface Breadcrumb {
  title: string
  href?: string // notice this is optional since not all breadcrumb components are clickable
  shouldUseExternalLink?: boolean
}

interface BreadcrumbsProps {
  breadcrumbs: Breadcrumb[]
}

export default function Breadcrumbs({ breadcrumbs }: BreadcrumbsProps): ReactElement {
  if (breadcrumbs.length > 1) {
    return (
      <Flex
        sx={{
          width: '100%',
          mb: '4.8rem',
          mt: ['2.4rem', 0],
          justifyContent: ['center', 'flex-start'],
        }}
      >
        <Flex
          sx={{
            fontSize: '1.8rem',
            fontWeight: 'bold',
            height: '4.2rem',
            backgroundColor: 'white10',
            px: '2.5rem',
            alignItems: 'center',
            borderRadius: '3rem',
          }}
        >
          {breadcrumbs.map((breadcrumb, index) => {
            const LinkComponent = breadcrumb.shouldUseExternalLink ? ExternalLink : InternalLink
            if (index === 0) {
              return (
                <LinkComponent
                  key={breadcrumb.title}
                  data-testid="help-breadcrumb"
                  href={breadcrumb.href}
                  sx={{
                    transition: theme => `${get(theme, 'transitions.default')}`,
                    display: 'flex',
                    gap: '1rem',
                    color: 'white50',
                    ':hover': {
                      transition: theme => `${get(theme, 'transitions.default')}`,
                      color: 'white',
                      '.home-icon': {
                        opacity: 1,
                        transition: theme => `${get(theme, 'transitions.default')}`,
                      },
                    },
                    fontSize: '1.7rem',
                    alignItems: 'center',
                  }}
                >
                  <Svg
                    className="home-icon"
                    svg={HomeIcon}
                    sx={{
                      opacity: 0.5,
                      transition: theme => `${get(theme, 'transitions.default')}`,
                    }}
                  />
                  <Text>{breadcrumb.title}</Text>
                </LinkComponent>
              )
            }
            return (
              <Flex
                data-testid="category-breadcrumb"
                key={breadcrumb.title}
                sx={{ alignItems: 'center' }}
              >
                {breadcrumb.href ? (
                  <>
                    <Text color="white50" sx={{ fontSize: '2rem', mx: '1rem' }}>
                      &#62;
                    </Text>
                    <LinkComponent href={breadcrumb.href}>
                      <Text
                        data-testid="category-breadcrumb-text"
                        sx={{
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflowX: 'hidden',
                          textOverflow: 'ellipsis',
                          transition: theme => `${get(theme, 'transitions.default')}`,
                          color: 'white50',
                          ':hover': {
                            color: 'white',
                          },
                        }}
                      >
                        {breadcrumb.title}
                      </Text>
                    </LinkComponent>
                  </>
                ) : (
                  <Flex sx={{ alignItems: 'center' }}>
                    <Svg
                      svg={BreadcrumbArrow}
                      sx={{
                        marginLeft: '0.8rem',
                        marginRight: '1.6rem',
                        minWidth: '3.2rem',
                        minHeight: '4.2rem',
                      }}
                    />
                    <Text
                      data-testid="category-breadcrumb-text"
                      sx={{
                        width: '100%',
                        whiteSpace: 'nowrap',
                        overflowX: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {breadcrumb.title}
                    </Text>
                  </Flex>
                )}
              </Flex>
            )
          })}
        </Flex>
      </Flex>
    )
  } else {
    return <Flex></Flex>
  }
}
